import React from 'react';
import { Button, Typography, Modal, Box, Grid, TextField, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './popup.module.css';
import { useTranslation } from 'react-i18next';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  icon?: JSX.Element;
  reasonLabel?: string;
  reasonValue?: string;
  onReasonChange?: (value: string) => void;
  disableConfirm?: boolean;
  confirmText?: string;
  cancelText?: string;
  useHeaderLayout?: boolean;
}

const IndentApproveCancelDialog: React.FC<DialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  icon,
  reasonLabel,
  reasonValue,
  onReasonChange,
  disableConfirm,
  confirmText,
  cancelText,
  useHeaderLayout = false,
}) => {
  const { t } = useTranslation('purchase_requisition');
  return (
    <Modal open={open} onClose={onClose}>
      <Box className={`${styles.modal} ${onReasonChange ? styles.modalMedium : styles.modalSmall}`}>
        {useHeaderLayout ? (
          <>
            {/* Header with title on left and close icon on right */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '16px 24px',
                borderBottom: '1px solid #E0E0E0'
              }}
            >
              <Typography className={styles.modalTitle} sx={{ textAlign: 'left', margin: 0 }}>
                {title}
              </Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            
            {/* Content area */}
            <Box sx={{ padding: '24px' }}>
              {onReasonChange && (
                <TextField
                  fullWidth
                  size='small'
                  label={reasonLabel ?? 'Reason'}
                  value={reasonValue ?? ''}
                  onChange={(e) => onReasonChange(e.target.value)}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                    },
                    mt: 1,
                    mb: 1,
                  }}
                />
              )}
            </Box>
          </>
        ) : (
          <Grid 
            container 
            spacing={2} 
            className={styles.modalBody} 
            style={{ 
              textAlign: 'center',
              padding: onReasonChange ? '24px' : '16px'
            }}
          >
            {icon && (
              <Grid item xs={12} md={12}>
                <span className={styles.infoImg}>{icon}</span>
              </Grid>
            )}
            <Grid item xs={12} md={12}>
              <Typography className={styles.modalTitle}>{title}</Typography>
            </Grid>
            <Grid item xs={12} md={12}>
              <h5 className={styles.modaldescription}>{message}</h5>
            </Grid>
            {onReasonChange && (
              <Grid item xs={12} md={12}>
                <TextField
                  fullWidth
                  size='small'
                  label={reasonLabel ?? 'Reason'}
                  value={reasonValue ?? ''}
                  onChange={(e) => onReasonChange(e.target.value)}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: 'Montserrat',
                      fontSize: '14px',
                    },
                    mt: 1,
                    mb: 1,
                  }}
                />
              </Grid>
            )}
          </Grid>
        )}
        <Box className={styles.modalActions}>
          <Button variant='outlined' onClick={onClose} className={styles.modalCancelBtn}>
            {cancelText ?? t('buttons.cancel', 'Cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={onConfirm}
            className={styles.modalSaveBtn}
            disabled={Boolean(disableConfirm)}
          >
            {confirmText ?? t('yes', 'Yes')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default IndentApproveCancelDialog;
